import { buildAffiliateLink } from '@/lib/affiliates/builders';

/**
 * Parse validity string to number of days
 * @param {string} validity - Validity string (e.g., "30 days", "1 month")
 * @returns {number} Number of days
 */
function parseValidityToDays(validity) {
    if (!validity) return 30;

    const lowerValidity = validity.toLowerCase();

    // Check for months
    if (lowerValidity.includes('month')) {
        const monthMatch = lowerValidity.match(/(\d+)\s*month/);
        if (monthMatch) {
            return parseInt(monthMatch[1]) * 30;
        }
    }

    // Check for days
    const dayMatch = lowerValidity.match(/(\d+)/);
    if (dayMatch) {
        return parseInt(dayMatch[1]);
    }

    return 30;
}

/**
 * Build regions array based on plan coverage data from Sanity
 * @param {Object} plan - Plan from Sanity with coverage and region data
 * @param {string} countrySlug - Country slug (e.g., 'montenegro', 'europe')
 * @param {string} countryName - Country name for display
 * @returns {Array} Array of region names
 */
function buildRegionsArray(plan, countrySlug, countryName) {
    const coverage = plan.coverage || 'local';

    // If coverage is regional and we have region data
    if (coverage === 'regional' && plan.region) {
        // Use countries from the region
        const countriesStr = plan.region.countriesEn || plan.region.countriesRu || '';
        if (countriesStr) {
            const countries = countriesStr.split(',').map(c => c.trim()).filter(c => c);
            if (countries.length > 0) {
                return countries;
            }
        }
        // Fallback to region name
        const regionName = plan.region.nameEn || plan.region.nameRu || 'Regional';
        return [regionName];
    }

    // If coverage is global
    if (coverage === 'global') {
        return ['Global'];
    }

    // Default: local coverage or Europe offerings
    if (countrySlug === 'europe') {
        return ['Europe'];
    }

    return [countryName];
}

/**
 * Convert Sanity plan to tariff format
 * @param {Object} plan - Plan from Sanity
 * @param {Object} provider - Provider from Sanity
 * @param {string} countrySlug - Country slug (e.g., 'montenegro', 'europe')
 * @param {string} countryName - Country name for display
 * @returns {Object} Converted tariff object
 */
export function convertSanityPlanToTariff(plan, provider, countrySlug, countryName = null) {
    const defaultCountryName = countryName || countrySlug || 'country';
    // Build regions array based on coverage data
    const regions = buildRegionsArray(plan, countrySlug, defaultCountryName);

    // Determine coverage type
    const coverage = plan.coverage || (countrySlug === 'europe' ? 'regional' : 'local');

    const planIdentifier =
        plan?._id ||
        plan?._key ||
        plan?.id ||
        (plan?.slug && (plan.slug.current || plan.slug)) ||
        null;

    const fallbackIdentifier = `${provider.code}-${countrySlug}-${plan.dataAmount || 'unlimited'}-${plan.validity}-${plan.priceAmount || 0}-${plan.features?.length || 0}`;
    const tariffId = planIdentifier || fallbackIdentifier;

    // Extract provider languages
    const providerLanguages = provider.languages
        ? provider.languages.split(',').map(lang => lang.trim().toLowerCase())
        : [];

    // Build affiliate link
    const baseUrl = plan.deepLink || provider.trackingBaseUrl;
    const affiliateLink = buildAffiliateLink({ provider, baseUrl });

    // Calculate duration in days
    const duration = parseValidityToDays(plan.validity);

    // Calculate price per GB and per day
    const pricePerGb = plan.dataAmount && !plan.isUnlimited
        ? (plan.priceAmount / plan.dataAmount).toFixed(2)
        : 0;

    const pricePerDay = plan.isUnlimited
        ? (plan.priceAmount / duration).toFixed(2)
        : null;

    return {
        id: tariffId,
        provider: provider.title,
        providerId: provider.code,
        providerLogo: provider.logoUrl,
        link: affiliateLink,
        duration: duration,
        data: plan.isUnlimited ? 0 : (plan.dataAmount || 0),
        dataAmount: plan.dataAmount || 0,
        dataUnit: plan.dataUnit || 'GB',
        validity: plan.validity,
        unlimited: plan.isUnlimited || false,
        isUnlimited: plan.isUnlimited || false,
        isPayAsYouGo: plan.isPayAsYouGo || false,
        calls: plan.callsAvailable || false,
        regions: regions,
        coverage: coverage,
        price: plan.priceAmount || 0,
        priceAmount: plan.priceAmount || 0,
        priceCurrency: plan.priceCurrency || 'EUR',
        pricePerGb: pricePerGb,
        pricePerDay: pricePerDay,
        languages: providerLanguages,
        features: plan.features || []
    };
}
